import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PayablesService } from '../../../../services/payables/payables.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { Payable } from '../../../../models/entities/payable';
import { Category } from '../../../../models/entities/accounting/category';
import { Equity } from '../../../../models/entities/accounting/equity';
import { FixedAsset } from '../../../../models/entities/accounting/fixed-asset';
import { Liability } from '../../../../models/entities/accounting/liability';
import moment from 'moment-timezone';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { finalize, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PayableFulfillmentType } from '../../../../models/entities/payable-fulfillment';
import { PayableState } from '../../../../enums/payables/payable-states.enum';
import { CommonModule } from '@angular/common';
import { ExpenseCategoriesFormComponent } from '../../../shared/expense-categories-form/expense-categories-form.component';
import { StringValidatorsModule } from '../../../../directives/validators/string/string-validators.module';
import { BankAccountSelectorComponent } from '../../shared/bank-account-selector/bank-account-selector.component';
import { PayableStatusBadgeComponent } from '../payables-dashboard/payable-status-badge/payable-status-badge.component';
import { ControlMessagesComponent } from '../../../shared/control-messages/control-messages.component';
import { PayableActionsService } from '../../../../services/payables/payable-actions.service';

@UntilDestroy()
@Component({
    selector: 'app-payable-update',
    templateUrl: './payable-update.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ExpenseCategoriesFormComponent,
        StringValidatorsModule,
        BankAccountSelectorComponent,
        PayableStatusBadgeComponent,
        ControlMessagesComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PayableUpdateComponent implements OnInit
{
    @Input() payableId: number;
    @Output() formSubmitted: EventEmitter<void> = new EventEmitter<void>();
    @Output() loadingStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    payable: Payable;
    payableForm: UntypedFormGroup;
    isSubmitting: boolean = false;
    isLoadingPayable: boolean = true;
    jwt: JwtLegFiClaims;
    minDate: Date;

    // Properties for expense categories form
    hasAccrualAccounting: boolean = false;
    invalidControls: string[] = [];
    expenseCategoriesLoading: boolean = false;
    defaultCategory: Category | Equity | FixedAsset | Liability = null;
    expenseCategoriesValid: boolean = true;
    expenseCategoriesError: string = null;

    constructor(
        private fb: UntypedFormBuilder,
        private router: Router,
        private payablesService: PayablesService,
        private growlerService: GrowlerService,
        private payableActionsService: PayableActionsService
) {
        this.jwt = LegFiJwtService.read();
        this.minDate = this.payablesService.minScheduledPaymentDate();
        this.initForm();
    }


    get minDay(): string {
        return moment(this.minDate).format('YYYY-MM-DD');
    }

    get totalAmount(): number {
        return this.payableForm?.get('amount')?.value || this.payable?.amount || 0;
    }

    get hasNoTransactions(): boolean {
        return (this.payable.transactions?.length ?? 0) === 0;
    }

    get hasBeenSent(): boolean {
        return this.payable?.currentFulfillment?.hasBeenSent || false;
    }

    get isCheck(): boolean {
        return this.payableActionsService.isCheckPayment(this.payable);
    }

    get requiresVerifiedBankAccount(): boolean {
        return this.payable?.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_PRINTED_CHECK ||
                this.payable?.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_LOB_BILL_PAYMENT;
    }

    get isDraft(): boolean {
        return this.payable?.status === PayableState.DRAFT;
    }

    get isTreasuryAch(): boolean {
        return this.payable?.currentFulfillment?.fulfillmentType === PayableFulfillmentType.FULFILLMENT_TYPE_TREASURY_ACH;
    }

    ngOnInit(): void {
        // Load payable data - component starts in loading state
        this.loadingStateChange.emit(true);

        this.payablesService.getPayable(this.payableId).pipe(untilDestroyed(this)).subscribe({
            next: (payable) => {
                this.payable = payable;
                this.patchFormValues(this.payable);
                this.isLoadingPayable = false;
                this.loadingStateChange.emit(this.isSubmitting); // Only loading if submitting
            },
            error: (error) => {
                this.growlerService.oops('Error loading payable details');
                this.isLoadingPayable = false;
                this.loadingStateChange.emit(false);
            }
        });
    }

    onSubmit(): void {
        if (this.payableForm.invalid || !this.expenseCategoriesValid) {
            this.payableForm.markAllAsTouched();
            this.growlerService.oops('Please fix validation errors before submitting');
            return;
        }

        this.isSubmitting = true;
        this.loadingStateChange.emit(true);

        const formData = this.payableForm.value;

        // If amount field is disabled, get its value explicitly
        const amount = this.payableForm.get('amount').disabled
            ? this.payableForm.get('amount').value
            : formData.amount;

        const timezone = LegFiJwtService.getTimezone();

        // Format dates to YYYY-MM-DD using timezone-aware formatting
        const formattedData = {
            ...formData,
            organizationId: this.jwt.orgId,
            amount: amount || 0,
            dueDate: formData.dueDate ? moment.tz(formData.dueDate, timezone).format('YYYY-MM-DD') : null,
            invoiceDate: formData.invoiceDate ? moment.tz(formData.invoiceDate, timezone).format('YYYY-MM-DD') : null,
            scheduledPaymentDate: formData.scheduledPaymentDate ? moment.tz(formData.scheduledPaymentDate, timezone).format('YYYY-MM-DD') : null,
            transactionDate: formData.transactionDate ? moment.tz(formData.transactionDate, timezone).format('YYYY-MM-DD') : null,
            expenseCategories: formData.expenseCategories?.map(split => ({
                expenseCategoryId: split.expenseCategory?.id,
                expenseCategoryType: split.expenseCategory?.accountType,
                amount: split.expenseCategoryAmount,
            })) || [],
            unifiedBankAccountId: formData.unifiedBankAccountId?.id || formData.unifiedBankAccountId || null
        };

        if (this.isCheck && this.payableForm.get('checkNumber')?.enabled) {
            const checkNumber = this.payableForm.get('checkNumber')?.value;
            if (checkNumber) {
                formattedData['checkNumber'] = checkNumber;
            }
        }

        // SD-8075: locked payment facts for Treasury Balance payables — never send them
        // (the API ignores them for treasury too; the disabled controls serialize as nulls otherwise)
        if (this.isTreasuryAch) {
            delete formattedData.transactionDate;
            delete formattedData.unifiedBankAccountId;
        }

        this.payablesService.updatePayableDetails(this.payableId, formattedData).pipe(take(1), finalize(() => {
                this.isSubmitting = false;
                this.loadingStateChange.emit(false);
        })).subscribe({
            next: () => {
                this.growlerService.success('Success', 'Payable updated successfully');
                this.formSubmitted.emit();
            },
            error: (error) => {
                this.growlerService.oops('Error updating payable');
            }
        });
    }

    cancel(): void {
        this.router.navigate(['/accounts-payable']);
    }

    onExpenseCategoriesLoadingChange(loading: boolean): void {
        this.expenseCategoriesLoading = loading;
    }

    onExpenseCategoriesValidationChange(validationState: { isValid: boolean, errorMessage?: string }): void {
        this.expenseCategoriesValid = validationState.isValid;
        this.expenseCategoriesError = validationState.errorMessage || null;

        // Update form validity
        if (validationState.isValid) {
            this.invalidControls = this.invalidControls.filter(control => control !== 'splitAmountMismatch');
        } else {
            if (!this.invalidControls.includes('splitAmountMismatch')) {
                this.invalidControls.push('splitAmountMismatch');
            }
        }
    }

    private patchFormValues(payable: Payable): void {
        const checkNumber = payable?.currentFulfillment?.fulfillmentReference?.checkNumber || null;

        const formValues = {
            amount: payable.amount || 0,
            dueDate: payable.dueDate ? this.toDay(payable.dueDate) : null,
            invoiceDate: payable.invoiceDate ? this.toDay(payable.invoiceDate) : null,
            invoiceNumber: payable.invoiceNumber,
            memo: payable.memo,
            scheduledPaymentDate: payable.scheduledPaymentDate ? this.toDay(payable.scheduledPaymentDate) : null,
            transactionDate: payable.transactions?.length > 0 ? this.toDay(payable.transactions[0].transactionDate) : null,
            unifiedBankAccountId: payable?.currentFulfillment?.fundingBankAccount?.id || null,
            checkNumber: checkNumber
        };

        this.payableForm.patchValue(formValues);

        // Set the default category if we have vendor information
        if (payable.vendor?.category) {
            this.defaultCategory = payable.vendor.category;
        }

        if (this.hasNoTransactions) {
            this.payableForm.get('transactionDate').disable();
            this.payableForm.get('unifiedBankAccountId').disable();
        }

        if (this.hasBeenSent || payable.allowedTransitions.length === 0) {
            this.payableForm.get('scheduledPaymentDate').disable();
            this.payableForm.get('amount').disable();
            this.payableForm.get('checkNumber')?.disable();
        }

        // SD-8075: Treasury Balance payments mirror Stripe — the transaction date and bank
        // account are payment facts and are locked server-side too. Amount/scheduled date stay
        // governed by the hasBeenSent rule above (pre-send ACH rescheduling remains supported).
        if (this.isTreasuryAch) {
            this.payableForm.get('transactionDate').disable();
            this.payableForm.get('unifiedBankAccountId').disable();
        }
    }

    private initForm(): void {
        this.payableForm = this.fb.group({
            amount: [0],
            dueDate: [null],
            invoiceDate: [null],
            invoiceNumber: [null],
            memo: [null],
            scheduledPaymentDate: [null],
            transactionDate: [null],
            expenseCategories: this.fb.array([]),
            unifiedBankAccountId: [null],
            checkNumber: [null, [
                Validators.min(1),
                Validators.max(500000000)
            ]]
        });
    }


    private toDay(momentDate: moment.Moment): string {
        return momentDate.format('YYYY-MM-DD');
    }
}
